import React from 'react';
import { inject, observer } from 'mobx-react';
import { injectIntl } from 'react-intl';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import FeedbackChain from './models/FeedbackChain';
import { withRouter } from 'react-router-dom';
import { Popover, OverlayTrigger, Media } from 'react-bootstrap';
import Priority from './FeedbackPriority';
import Comment from './Comment';

@withRouter
@injectIntl
@inject('rootStore', 'feedbackChainsStore')
@observer
export default class FeedbacksTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hoveredFeedback: undefined
    };
  }

  render() {
    const data = this.props.feedbackChainsStore.filteredFeedbackChains;

    const { intl } = this.props;
    let feedbackInfoPopover = null;
    if (this.state.hoveredFeedback) {
      const feedbackChain = this.state.hoveredFeedback;
      const lastComment = feedbackChain.lastComment;
      let commentsMessage = lastComment
        ? intl.formatMessage({ id: 'last_comment' })
        : intl.formatMessage({ id: 'no_comments' });
      feedbackInfoPopover = (
        <Popover id="feedback-info-popover">
          <div>
            <Media>
              <Media.Left>
                <div className={'img-thumbnail img-circle'}>
                  <img
                    className={'img-responsive'}
                    width={50}
                    height={50}
                    src={feedbackChain.creator.profilePhotoUrl}
                    alt={feedbackChain.creator.name + ' thumbnail'}
                  />
                </div>
                {feedbackChain.creator.name}
              </Media.Left>
              <Media.Body>
                <span className={'bold'}>{feedbackChain.title}</span>
                <p>{feedbackChain.description}</p>
              </Media.Body>
            </Media>
            <div>
              <hr />
              <p className={'bold text-muted'}>{commentsMessage}</p>
              {lastComment && <Comment comment={lastComment} notEditable />}
            </div>
          </div>
        </Popover>
      );
    }

    return (
      <div>
        <ReactTable
          data={data}
          previousText={intl.formatMessage({ id: 'react_table_previous_text' })}
          nextText={intl.formatMessage({ id: 'react_table_next_text' })}
          loadingText={intl.formatMessage({ id: 'react_table_loading_text' })}
          noDataText={intl.formatMessage({ id: 'react_table_no_data_text' })}
          pageText={intl.formatMessage({ id: 'react_table_page_text' })}
          ofText={intl.formatMessage({ id: 'react_table_of_text' })}
          rowsText={intl.formatMessage({ id: 'react_table_rows_text' })}
          loading={this.props.feedbackChainsStore.loading}
          columns={[
            {
              //Header: "Elements that require your attention",
              columns: [
                {
                  Header: intl.formatMessage({ id: 'priority' }),
                  accessor: 'priority',
                  Cell: props => (
                    <div className={'text-center'}>
                      <Priority priority={props.value} />
                    </div>
                  ),
                  sortMethod: (elA, elB, desc) => {
                    const a = FeedbackChain.priorities.indexOf(elA);
                    const b = FeedbackChain.priorities.indexOf(elB);
                    if (a > b) {
                      return 1;
                    }
                    if (a < b) {
                      return -1;
                    }
                    return 0;
                  },
                  maxWidth: 80
                },
                {
                  Header: intl.formatMessage({ id: 'id' }),
                  accessor: 'id',
                  Cell: props => (
                    <span className={'bold'}>{'#' + props.value}</span>
                  ),
                  maxWidth: 50
                },
                {
                  Header: intl.formatMessage({ id: 'title' }),
                  accessor: 'title',
                  minWidth: 500,
                  maxWidth: 500,
                  Cell: props => (
                    <OverlayTrigger
                      trigger={['hover', 'focus']}
                      placement={'top'}
                      overlay={feedbackInfoPopover}
                    >
                      <div className={'title-cell-value'}>{props.value}</div>
                    </OverlayTrigger>
                  )
                },
                {
                  Header: intl.formatMessage({ id: 'creator' }),
                  accessor: 'creator.name',
                  minWidth: 100
                },
                {
                  Header: intl.formatMessage({ id: 'updated_at' }),
                  id: 'updatedAt',
                  accessor: 'updatedAt',
                  Cell: props => <span>{props.value.format('LL')}</span>,
                  sortMethod: (elA, elB, desc) => {
                    const a = elA;
                    const b = elB;
                    if (a.diff(b) > 0) {
                      return -1;
                    }
                    if (a.diff(b) == 0) {
                      return 0;
                    }
                    return 1;
                  },
                  minWidth: 100
                },
                {
                  Header: intl.formatMessage({ id: 'category' }),
                  accessor: 'category',
                  Cell: props => (
                    <span>{intl.formatMessage({ id: props.value })}</span>
                  ),
                  minWidth: 100
                },
                {
                  Header: intl.formatMessage({ id: 'assignees' }),
                  id: 'assignees',
                  accessor: fc =>
                    fc.assignees.length > 0
                      ? fc.assignees
                          .slice(1)
                          .reduce(
                            (accumulated, item) =>
                              accumulated + ' / ' + item.name,
                            fc.assignees[0].name
                          )
                      : '-',
                  minWidth: 100
                }
              ]
            }
          ]}
          defaultSorted={[
            {
              id: ['isRecentlyDone', 'isDone'].includes(
                this.props.feedbackChainsStore.filter
              )
                ? 'updatedAt'
                : 'priority',
              desc: false
            }
          ]}
          defaultPageSize={this.props.feedbackChainsStore.pageSize}
          onPageSizeChange={p =>
            this.props.feedbackChainsStore.pageSizePreference(p)
          }
          className="-highlight feedback-chains-table"
          getTdProps={this.handleTdClick}
        />
      </div>
    );
  }

  handleTdClick = (state, rowInfo, column, instance) => {
    return {
      onClick: (e, handleOriginal) => {
        const feedbackChain = rowInfo.original;
        const { intl } = this.props;
        this.props.history.push(
          '/' +
            intl.locale +
            '/gstartup/' +
            this.props.rootStore.startupId +
            '/igsupport/chain/' +
            feedbackChain.id
        );
        if (handleOriginal) {
          handleOriginal();
        }
      },

      onMouseOver: (e, handleOriginal) => {
        if (rowInfo != undefined) {
          const feedbackChain = rowInfo.original;
          this.setState({ hoveredFeedback: feedbackChain });
          if (handleOriginal) {
            handleOriginal();
          }
        }
      }
    };
  };
}
